import { DEFAULT_SEED, createSeededRuntime } from "@/lib/seeded";
import { placeOrder } from "./actions";
import { makeSeed, type YJMockData } from "./seed";
import { brokerId, customerId, productId, ticketId, type Customer, type ScenarioId, type Ticket } from "./types";

type ScenarioDefinition = {
  id: ScenarioId;
  label: string;
  purpose: string;
  build: (state: YJMockData) => YJMockData;
};

const scenarioCustomers: Array<Omit<Customer, "id">> = [
  { name: "อารยา พ.", phone: "087-100-0001", addr: "ลาดพร้าว กรุงเทพฯ" },
  { name: "ปวริศา ธ.", phone: "087-100-0002", addr: "เมือง นครราชสีมา" },
  { name: "ณัฐพล ร.", phone: "087-100-0003", addr: "เมือง ภูเก็ต" },
  { name: "วิภาวี จ.", phone: "087-100-0004", addr: "บางแค กรุงเทพฯ" },
  { name: "ชนาภา ม.", phone: "087-100-0005", addr: "เมือง อุดรธานี" },
];

function repeatOrders(state: YJMockData, count: number) {
  let next = state;
  const products = next.products.filter((product) => product.stock > 0);
  const brokers = next.brokers;
  for (let i = 0; i < count; i += 1) {
    const product = products[i % products.length];
    const broker = brokers[i % brokers.length];
    const customer = scenarioCustomers[i % scenarioCustomers.length];
    next = placeOrder(next, {
      productId: product.id,
      brokerId: broker.id,
      paymentMethod: i % 3 === 0 ? "QR" : i % 3 === 1 ? "CARD" : "COD",
      customer: { ...customer, phone: customer.phone.replace(/.$/, String(i % 10)) },
    }).state;
  }
  return next;
}

function addOpenTicket(state: YJMockData, index: number, category: string, subject: string) {
  const counter = state.counters.ticket + 1;
  const at = state.clock.now + state.clock.stepMs;
  const ticket: Ticket = {
    id: ticketId(`t${counter}`),
    subject,
    category,
    priority: index % 3 === 0 ? "high" : "medium",
    status: "open",
    openedBy: index % 2 === 0 ? "Vita Plus" : "Glow Beauty",
    createdAt: at,
    messages: [{ from: "brand", text: "ต้องการให้ทีมตรวจสอบรายการนี้ด่วน", at }],
  };
  return {
    ...state,
    clock: { ...state.clock, now: at },
    counters: { ...state.counters, ticket: counter },
    tickets: [ticket, ...state.tickets],
  };
}

function highGmv(state: YJMockData) {
  let next = repeatOrders(state, state.orders.length * 2);
  next = repeatOrders(next, next.leads.length);
  return {
    ...next,
    brokers: next.brokers.map((broker) => ({ ...broker, exp: Math.round(broker.exp * 1.5) })),
    scenarioConfig: { notes: ["3x order pressure", "2x lead pressure", "broker EXP boosted"] },
  };
}

function leadBacklog(state: YJMockData) {
  let next = state.leads.length >= 50 ? state : repeatOrders(state, 50 - state.leads.length);
  next = {
    ...next,
    leads: next.leads.map((lead) => ({ ...lead, status: "pending", assignedTo: null, attempts: 0 })),
    scenarioConfig: { agents: ["Agent-021", "Agent-044"], notes: ["50 pending leads", "only two agents available"] },
  };
  return next;
}

function lowStock(state: YJMockData) {
  return {
    ...state,
    products: state.products.map((product, index) => {
      if (index < 3) return { ...product, stock: 0 };
      if (index < 8) return { ...product, stock: index + 1 };
      return product;
    }),
    scenarioConfig: { notes: ["3 products out of stock", "5 products below 10 stock"] },
  };
}

function supplierDispute(state: YJMockData) {
  let next = state;
  for (let i = 0; i < 10; i += 1) {
    next = addOpenTicket(next, i, "แบรนด์", `ข้อพิพาทยอดขาย/GP รอบที่ ${i + 1}`);
  }
  return { ...next, scenarioConfig: { notes: ["10 open brand-category tickets"] } };
}

function payoutPending(state: YJMockData) {
  return {
    ...state,
    brokers: state.brokers.map((broker, index) =>
      index < 5 ? { ...broker, withdrawn: Math.max(0, broker.withdrawn - 3_000) } : broker,
    ),
    supplierPayouts: state.supplierPayouts.map((payout, index) =>
      index < 3
        ? {
            ...payout,
            withdrawn: Math.max(0, payout.withdrawn - 5_000),
            history: [{ d: state.clock.now, a: 5_000, ref: `WD-PENDING-${index + 1}`, status: "pending" as const }, ...(payout.history ?? [])],
          }
        : payout,
    ),
    scenarioConfig: { notes: ["5 brokers and 3 suppliers have pending payout pressure"] },
  };
}

function campaignSpike(state: YJMockData) {
  return {
    ...state,
    campaigns: state.campaigns.map((campaign, index) =>
      index < 2
        ? {
            ...campaign,
            spent: Math.round(campaign.budget * 0.8),
            gmv: campaign.gmv * 5,
          }
        : campaign,
    ),
    scenarioConfig: { notes: ["2 campaigns at 80% budget", "campaign GMV x5"] },
  };
}

function paymentFail(state: YJMockData) {
  return {
    ...state,
    currentBrokerId: brokerId("br1"),
    currentBrandId: state.currentBrandId,
    scenarioConfig: { paymentFailEvery: 3, notes: ["Mock API throws on every third placeOrder call"] },
  };
}

function pdpaDecline(state: YJMockData) {
  return {
    ...state,
    currentBrokerId: brokerId("br2"),
    currentBrandId: state.currentBrandId,
    scenarioConfig: { pdpaDeclined: true, notes: ["Customer rejects PDPA consent during checkout"] },
  };
}

export const SCENARIOS: Record<ScenarioId, ScenarioDefinition> = {
  normal: {
    id: "normal",
    label: "Normal",
    purpose: "Default moderate traffic and operations",
    build: (state) => state,
  },
  "high-gmv": {
    id: "high-gmv",
    label: "High GMV",
    purpose: "Heavy traffic and high order pressure",
    build: highGmv,
  },
  "lead-backlog": {
    id: "lead-backlog",
    label: "Lead Backlog",
    purpose: "Nice Call queue pressure",
    build: leadBacklog,
  },
  "low-stock": {
    id: "low-stock",
    label: "Low Stock",
    purpose: "Supplier inventory crisis",
    build: lowStock,
  },
  "supplier-dispute": {
    id: "supplier-dispute",
    label: "Supplier Dispute",
    purpose: "Support flood from brand disputes",
    build: supplierDispute,
  },
  "payout-pending": {
    id: "payout-pending",
    label: "Payout Pending",
    purpose: "Money flow stress",
    build: payoutPending,
  },
  "campaign-spike": {
    id: "campaign-spike",
    label: "Campaign Spike",
    purpose: "Marketing surge with high ROAS",
    build: campaignSpike,
  },
  "payment-fail": {
    id: "payment-fail",
    label: "Payment Fail",
    purpose: "Checkout API failure edge case",
    build: paymentFail,
  },
  "pdpa-decline": {
    id: "pdpa-decline",
    label: "PDPA Decline",
    purpose: "Consent declined edge case",
    build: pdpaDecline,
  },
};

export function createScenarioState(scenarioId: ScenarioId = "normal", seed = DEFAULT_SEED) {
  const runtime = createSeededRuntime(`${seed}:${scenarioId}`);
  const base = makeSeed(runtime, scenarioId);
  const state = SCENARIOS[scenarioId].build(base);
  return {
    ...state,
    currentScenario: scenarioId,
    clock: { ...state.clock, seed: `${seed}:${scenarioId}` },
  };
}

export function getScenarioOptions() {
  return Object.values(SCENARIOS).map(({ id, label, purpose }) => ({ id, label, purpose }));
}

export const scenarioIds = Object.keys(SCENARIOS) as ScenarioId[];

export const sampleCheckoutCustomer = {
  customerId: customerId("c1"),
  productId: productId("p1"),
  brokerId: brokerId("br1"),
};
